import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Sparkles, Check, X, Send, Eye, AlertTriangle, ExternalLink, Image as ImageIcon, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/date";

export default function AutomationReviewTab({ sources = [], onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [categories, setCategories] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async (opts = {}) => {
    if (!opts._silent) setLoading(true);
    try {
      const [detected, review, approved, cats] = await Promise.all([
        api.adminAutoListImported({ status: "detected", limit: 50 }),
        api.adminAutoListImported({ status: "ready_for_review", limit: 50 }),
        api.adminAutoListImported({ status: "approved", limit: 20 }),
        api.categories().catch(() => []),
      ]);
      if (opts._cancelled?.()) return;
      const all = [...(review.items || []), ...(approved.items || []), ...(detected.items || [])];
      setItems(all);
      setCategories(cats || []);
      if (!selectedId && all.length) setSelectedId(all[0].id);
    } catch (e) { /* ignore */ }
    finally { if (!opts._cancelled?.()) setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    load({ _cancelled: () => cancelled });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!selectedId) { setSelected(null); return; }
    api.adminAutoGetImported(selectedId)
      .then((it) => { if (!cancelled) { setSelected(it); setDirty(false); } })
      .catch(() => { if (!cancelled) setSelected(null); });
    return () => { cancelled = true; };
  }, [selectedId]);

  if (loading) return <div className="p-6 text-textgray"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando…</div>;
  if (items.length === 0) return <div className="p-8 text-center text-textgray">No hay noticias en revisión. Ejecutá una fuente activa para empezar.</div>;

  const setField = (patch) => { setSelected((s) => ({ ...s, ...patch })); setDirty(true); };
  const nameFor = (sid) => sources.find((s) => s.id === sid)?.name || "—";
  const alerts = selected?.ai_alerts || {};
  const sensitive = (alerts.sensitive_content_flags || []).length > 0;

  const runAi = async () => {
    if (!selected) return;
    setProcessing(true);
    try {
      const updated = await api.adminAutoProcessAi(selected.id);
      setSelected(updated); setDirty(false);
      toast.success("Procesada con IA");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error al procesar con IA");
    } finally { setProcessing(false); }
  };

  const saveEdits = async () => {
    if (!selected) return;
    const patch = {
      generated_title: selected.generated_title,
      generated_summary: selected.generated_summary,
      generated_content: selected.generated_content,
      generated_category: selected.generated_category,
      generated_city: selected.generated_city,
      generated_tags: selected.generated_tags,
      generated_social_facebook: selected.generated_social_facebook,
      generated_social_instagram: selected.generated_social_instagram,
      generated_image_url: selected.generated_image_url,
      category_id: selected.category_id,
      author_name: selected.author_name,
      custom_source_attribution: selected.custom_source_attribution,
      original_published_at: selected.original_published_at,
      image_caption: selected.image_caption,
      image_credit: selected.image_credit,
    };
    try {
      const u = await api.adminAutoUpdateImported(selected.id, patch);
      setSelected(u); setDirty(false);
      toast.success("Cambios guardados");
    } catch (e) { toast.error("No se pudo guardar"); }
  };

  const approve = async () => {
    if (dirty) await saveEdits();
    try {
      const u = await api.adminAutoUpdateImported(selected.id, { status: "approved" });
      setSelected(u); toast.success("Noticia aprobada"); load(); onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo aprobar");
    }
  };

  const reject = async () => {
    const reason = window.prompt("Motivo del rechazo (opcional):") || undefined;
    try {
      const u = await api.adminAutoUpdateImported(selected.id, { status: "rejected", rejection_reason: reason });
      setSelected(u); toast.success("Rechazada"); load(); onChanged?.();
    } catch (e) { toast.error("No se pudo rechazar"); }
  };

  const publish = async () => {
    if (!selected.category_id) {
      toast.error("Debe seleccionar una categoría antes de publicar");
      return;
    }
    if (dirty) await saveEdits();
    setPublishing(true);
    try {
      const r = await api.adminAutoPublish(selected.id, { publish: true, category_id: selected.category_id });
      toast.success(`Publicada: ${r.article.slug}`);
      load(); onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo publicar");
    } finally { setPublishing(false); }
  };

  return (
    <section className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      {/* List */}
      <aside className="lg:col-span-1 bg-white border border-softblue max-h-[80vh] overflow-y-auto">
        <div className="p-3 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray">
          {items.length} noticia(s)
        </div>
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => setSelectedId(it.id)}
            className={`w-full text-left p-3 border-b border-softblue/60 hover:bg-lightgray/60 ${selectedId === it.id ? "bg-lightgray/70" : ""}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-textgray">{it.status}</div>
            <div className="text-sm font-semibold text-blueblack line-clamp-2">
              {it.generated_title || it.original_title || "(sin título)"}
            </div>
            <div className="text-[11px] text-textgray mt-0.5">{nameFor(it.source_id)}</div>
          </button>
        ))}
      </aside>

      {/* Detail */}
      <div className="lg:col-span-3 space-y-4">
        {!selected ? (
          <div className="p-6 text-textgray">Seleccioná una noticia.</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{selected.status}</Badge>
              {sensitive && (
                <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50">
                  <AlertTriangle className="w-3 h-3 mr-1" /> contenido sensible
                </Badge>
              )}
              {(alerts.factual_alerts || []).length > 0 && (
                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {alerts.factual_alerts.length} alerta(s)
                </Badge>
              )}
              {selected.date_validation_status !== "confirmed" && (
                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Fecha sin confirmar</Badge>
              )}
              <span className="text-[10px] uppercase tracking-widest text-textgray ml-auto">
                Detectada {formatDateTime(selected.detected_at)}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Original */}
              <div className="bg-white border border-softblue p-4 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold">Original</div>
                <h3 className="font-serif font-bold text-blueblack">{selected.original_title}</h3>
                {selected.original_summary && <p className="text-sm italic text-textgray">{selected.original_summary}</p>}
                <div className="text-xs text-textgray">{nameFor(selected.source_id)} · {selected.original_published_at || "sin fecha"}</div>
                {selected.original_image_url && (
                  <img src={selected.original_image_url} alt="" className="max-h-40 border border-softblue" />
                )}
                <div className="text-xs max-h-64 overflow-y-auto border border-softblue p-2 bg-lightgray/40" dangerouslySetInnerHTML={{ __html: selected.original_content || "" }} />
                {selected.source_url && (
                  <a href={selected.source_url} target="_blank" rel="noreferrer" className="text-xs text-turquoise hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Abrir original
                  </a>
                )}
              </div>

              {/* Generated / editable */}
              <div className="bg-white border border-turquoise/40 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-turquoise font-semibold flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Generado por IA (editable)
                  </div>
                  <Button size="sm" variant="outline" onClick={runAi} disabled={processing} className="gap-1">
                    {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {selected.generated_title ? "Reprocesar" : "Procesar con IA"}
                  </Button>
                </div>
                <Label className="text-[10px] text-textgray uppercase tracking-widest">Título</Label>
                <Input value={selected.generated_title || ""} onChange={(e) => setField({ generated_title: e.target.value })} />
                <Label className="text-[10px] text-textgray uppercase tracking-widest">Bajada</Label>
                <Textarea rows={2} value={selected.generated_summary || ""} onChange={(e) => setField({ generated_summary: e.target.value })} />
                <Label className="text-[10px] text-textgray uppercase tracking-widest">Cuerpo (HTML)</Label>
                <Textarea rows={10} value={selected.generated_content || ""} onChange={(e) => setField({ generated_content: e.target.value })} className="font-mono text-xs" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-textgray uppercase tracking-widest">Categoría sugerida (IA)</Label>
                    <Input value={selected.generated_category || ""} onChange={(e) => setField({ generated_category: e.target.value })} data-testid="review-generated-category" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-textgray uppercase tracking-widest">Ciudad</Label>
                    <Input value={selected.generated_city || ""} onChange={(e) => setField({ generated_city: e.target.value })} data-testid="review-generated-city" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-textgray uppercase tracking-widest">Categoría del portal (obligatoria para publicar)</Label>
                  <Select value={selected.category_id || ""} onValueChange={(v) => setField({ category_id: v || null })}>
                    <SelectTrigger data-testid="review-category-select">
                      <SelectValue placeholder="— Elegí una categoría —" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selected.generated_category && !selected.category_id && (
                    <div className="text-[11px] text-textgray italic mt-1">
                      Sugerencia IA: <span className="font-semibold text-blueblack">{selected.generated_category}</span> — confirmá seleccionando arriba.
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-textgray uppercase tracking-widest">Autor / firma (opcional)</Label>
                    <Input
                      data-testid="review-author-name"
                      value={selected.author_name || ""}
                      onChange={(e) => setField({ author_name: e.target.value })}
                      placeholder="(opcional)"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-textgray uppercase tracking-widest">Fuente (atribución pública, opcional)</Label>
                    <Input
                      data-testid="review-source-attribution"
                      value={selected.custom_source_attribution || ""}
                      onChange={(e) => setField({ custom_source_attribution: e.target.value })}
                      placeholder="(opcional — dejar vacío si no querés atribuir)"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-textgray uppercase tracking-widest">Fecha original de la noticia</Label>
                  <Input
                    type="datetime-local"
                    data-testid="review-original-date"
                    value={selected.original_published_at ? String(selected.original_published_at).slice(0, 16) : ""}
                    onChange={(e) => setField({ original_published_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  />
                </div>
                <Label className="text-[10px] text-textgray uppercase tracking-widest">Etiquetas (separadas por coma)</Label>
                <Input
                  data-testid="review-generated-tags"
                  value={(selected.generated_tags || []).join(", ")}
                  onChange={(e) => setField({ generated_tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 12) })}
                  placeholder="ushuaia, provincial, gobierno"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-textgray uppercase tracking-widest">Texto para Facebook</Label>
                    <Textarea rows={3} value={selected.generated_social_facebook || ""} onChange={(e) => setField({ generated_social_facebook: e.target.value })} data-testid="review-fb-text" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-textgray uppercase tracking-widest">Texto para Instagram</Label>
                    <Textarea rows={3} value={selected.generated_social_instagram || ""} onChange={(e) => setField({ generated_social_instagram: e.target.value })} data-testid="review-ig-text" />
                  </div>
                </div>
                {/* Image replacement — no AI-generated images. Editorial upload only. */}
                <div className="bg-lightgray/40 border border-softblue p-3 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" /> Imagen para publicar (pendiente de derechos)
                  </div>
                  <div className="flex items-start gap-3">
                    {(selected.generated_image_url || selected.original_image_url) ? (
                      <img
                        src={selected.generated_image_url || selected.original_image_url}
                        alt=""
                        className="w-32 h-24 object-cover border border-softblue"
                      />
                    ) : (
                      <div className="w-32 h-24 bg-white border border-softblue flex items-center justify-center text-textgray text-xs">
                        sin imagen
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          data-testid="review-image-upload"
                          className="text-xs"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const media = await api.adminUploadMedia(file, { source: "automation" });
                              setField({ generated_image_url: media?.url || media?.source_url || media?.presigned_url || media?.thumbnail_url });
                              toast.success("Imagen cargada — recordá confirmar derechos.");
                            } catch (err) {
                              toast.error("No se pudo subir la imagen");
                            }
                          }}
                        />
                      </label>
                      <Input
                        placeholder="Crédito / autor de la imagen"
                        value={selected.image_credit || ""}
                        onChange={(e) => setField({ image_credit: e.target.value })}
                        data-testid="review-image-credit"
                      />
                      <Input
                        placeholder="Epígrafe / caption"
                        value={selected.image_caption || ""}
                        onChange={(e) => setField({ image_caption: e.target.value })}
                        data-testid="review-image-caption"
                      />
                      <div className="text-[10px] italic text-textgray">
                        No se generan imágenes con IA. Confirmá derechos o autorización antes de publicar.
                      </div>
                      {selected.image_pending_review && (
                        <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 p-2" data-testid="review-image-pending">
                          Imagen pendiente de revisión ({selected.image_validation_reason || "dominio no autorizado"}). Cargala desde /admin/media o dejala vacía antes de publicar.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {(alerts.factual_alerts || []).length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 p-2">
                    <div className="font-semibold mb-1">Alertas factuales</div>
                    <ul className="list-disc list-inside">{alerts.factual_alerts.map((a, i) => <li key={`fa-${i}-${String(a).slice(0, 30)}`}>{a}</li>)}</ul>
                  </div>
                )}
                {(alerts.validation_errors || []).length > 0 && (
                  <div className="bg-red-50 border border-red-200 text-xs text-red-800 p-2" data-testid="review-validation-errors">
                    <div className="font-semibold mb-1">Errores de validación</div>
                    <ul className="list-disc list-inside">{alerts.validation_errors.map((a, i) => <li key={`ve-${i}-${String(a).slice(0, 30)}`}>{a}</li>)}</ul>
                  </div>
                )}
                {(alerts.missing_information || []).length > 0 && (
                  <div className="bg-lightgray/60 border border-softblue text-xs p-2">
                    <div className="font-semibold mb-1">Información faltante</div>
                    <ul className="list-disc list-inside">{alerts.missing_information.map((a, i) => <li key={`mi-${i}-${String(a).slice(0, 30)}`}>{a}</li>)}</ul>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={saveEdits} disabled={!dirty} data-testid="review-save">Guardar cambios</Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (dirty) await saveEdits();
                  try {
                    const p = await api.adminAutoPreview(selected.id);
                    setPreview(p);
                    setPreviewOpen(true);
                  } catch (e) {
                    toast.error("No se pudo generar la previsualización");
                  }
                }}
                data-testid="review-preview"
                className="gap-1"
              >
                <Eye className="w-4 h-4" /> Previsualizar
              </Button>
              <Button onClick={approve} className="bg-emerald-700 hover:bg-emerald-800 gap-1" data-testid="review-approve">
                <Check className="w-4 h-4" /> Aprobar
              </Button>
              <Button variant="outline" onClick={reject} className="border-red-200 text-red-700 hover:bg-red-50 gap-1" data-testid="review-reject">
                <X className="w-4 h-4" /> Rechazar
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  const dt = window.prompt("Fecha/hora de publicación (formato local, ej: 2026-07-25 09:00)");
                  if (!dt) return;
                  const iso = new Date(dt).toISOString();
                  if (dirty) await saveEdits();
                  try {
                    await api.adminAutoSchedule(selected.id, iso);
                    toast.success(`Programada para ${dt}`);
                    load(); onChanged?.();
                  } catch (e) {
                    const d = e?.response?.data?.detail;
                    toast.error(typeof d === "string" ? d : "No se pudo programar");
                  }
                }}
                disabled={!selected.category_id}
                data-testid="review-schedule"
                className="gap-1"
              >
                <Clock className="w-4 h-4" /> Programar
              </Button>
              <Button onClick={publish} disabled={publishing || !selected.category_id} className="gap-1 bg-turquoise hover:bg-turquoise/90" data-testid="review-publish">
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publicar
              </Button>
              {!selected.category_id && (
                <span className="text-xs text-amber-700 italic self-center" data-testid="review-no-category-hint">Seleccioná una categoría antes de publicar.</span>
              )}
              {sensitive && <span className="text-xs text-amber-700 italic self-center" data-testid="review-sensitive-hint">Aviso: contenido sensible detectado (se puede publicar).</span>}
            </div>
          </>
        )}
      </div>
      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="review-preview-dialog">
          <DialogHeader><DialogTitle>Previsualización editorial</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3">
              {preview.publication_ready ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs p-2">
                  ✓ Lista para publicar (todas las guardas editoriales pasan).
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2" data-testid="review-preview-blocker">
                  Bloqueada por: <strong>{preview.publication_blocker}</strong>. Corregí antes de publicar.
                </div>
              )}
              {preview.main_image && (
                <img src={preview.main_image} alt="" className="w-full max-h-72 object-cover" />
              )}
              <div className="kicker">{preview.category?.name || "— sin categoría —"}</div>
              <h1 className="font-serif text-2xl font-black text-blueblack">{preview.title}</h1>
              {preview.summary && <p className="text-base text-textgray italic">{preview.summary}</p>}
              {(preview.author_name || preview.custom_source_attribution || preview.scheduled_at) && (
                <div className="text-xs text-textgray">
                  {preview.author_name && <>Autor: {preview.author_name}</>}
                  {preview.author_name && preview.custom_source_attribution && ' · '}
                  {preview.custom_source_attribution && <>Fuente: {preview.custom_source_attribution}</>}
                  {preview.scheduled_at && ` · Programada: ${new Date(preview.scheduled_at).toLocaleString("es-AR")}`}
                </div>
              )}
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview.content || "" }} />
              {(preview.tags || []).length > 0 && (
                <div className="text-xs text-textgray">Etiquetas: {preview.tags.join(", ")}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
